import { ApplicationView, useController } from '@aha-app/mvc';
import React, { useState } from 'react';
import FieldsController from '../../../controllers/FieldsController';
import type SelectField from '../../../fields/SelectField';
import EditableTextField from '../../EditableTextField';
import styles from '../../../styles.module.css';
import Tippy from '@tippyjs/react';
import { HexColorPicker } from 'react-colorful';

interface SelectFieldEditorProps extends FieldEditorProps {
  field: SelectField;
}

const SelectFieldEditor: React.FC<SelectFieldEditorProps> = ({ field }) => {
  const controller = useController(FieldsController);
  const [showOptions, setShowOptions] = useState(false);

  let content: JSX.Element | null = null;

  if (showOptions) {
    content = (
      <div className={styles.itemList} style={{ flex: '1' }}>
        {field.options.items.map(option => (
          <div
            key={option.id}
            className={styles.flexRow}
            style={{ alignItems: 'center' }}
          >
            {field.tags && (
              <Tippy
                arrow={false}
                content={
                  <>
                    <HexColorPicker
                      color={option.color || '#FFFFFF'}
                      onChange={color =>
                        controller.actionUpdateSelectOption(field, option, {
                          color,
                        })
                      }
                    />
                    <input
                      className={styles.colorPickerHexInput}
                      value={option.color}
                      onChange={event =>
                        controller.actionUpdateSelectOption(field, option, {
                          color: event.target.value,
                        })
                      }
                    />
                  </>
                }
                theme='light'
                trigger='click'
                interactive
                duration={75}
                animation='scale'
                appendTo={document.body}
              >
                <div
                  style={{
                    aspectRatio: '1',
                    backgroundColor: option.color,
                    borderRadius: 3,
                    height: '100%',
                    // Line-height + vertical padding, can't get aspect ratio to work on Safari
                    width: 30,
                  }}
                ></div>
              </Tippy>
            )}

            <EditableTextField
              initialValue={option.name}
              onChange={name =>
                controller.actionUpdateSelectOption(field, option, { name })
              }
            />

            <aha-button
              kind='plain'
              class={styles.actionButton}
              onClick={() => {
                if (confirm('Are you sure you want to delete this option?')) {
                  controller.actionRemoveSelectOption(field, option);
                }
              }}
            >
              <i className='fa-regular fa-trash-can' />
            </aha-button>
          </div>
        ))}

        <div className={styles.flexRow} style={{}}>
          <div style={{ flex: 1 }} />

          <aha-button
            kind='plain'
            class='p-0'
            onClick={() => field.addOption({ name: 'New' })}
          >
            <i className='fa-regular fa-plus-circle' /> Add
          </aha-button>

          <div className={styles.actionButton}>&nbsp;</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.flexRow}>
      <div>
        <aha-button kind='plain' onClick={() => setShowOptions(!showOptions)}>
          <div className={styles.flexRow} style={{ alignItems: 'center' }}>
            <i
              className='fa-regular fa-chevron-right'
              style={{
                transition: 'transform 0.2s ease-in-out',
                transform: showOptions ? 'rotate(90deg)' : 'rotate(0deg)',
              }}
            />
            {showOptions ? <span>&#x200B;</span> : 'Edit values'}
          </div>
        </aha-button>
      </div>

      {content}
    </div>
  );
};

export default ApplicationView(SelectFieldEditor);
